import { useRef, useState } from 'react';
import type { FlightSegment, PriceScope } from '../../types';
import { fromMinor, money, toMinor, toUtcMillis } from '../../core';
import { newId, useActions, useStore } from '../../store';
import { TextField, DateTimeField, SelectField, MoneyField, NoteField } from './fields';
import fieldStyles from './fields.module.css';
import { COMMON_TIMEZONES } from './timezones';
import { flightLongLabel } from './flightLabel';
import { syncLinkedTo } from './syncLinkedTo';
import formShell from './formShell.module.css';
import { useT } from '../../i18n';

export interface FlightFormProps {
  mode: 'create' | 'edit';
  segment?: FlightSegment;
  onClose(): void;
}

/** Подписи берутся из словаря по ключу scope.<value>. */
const PRICE_SCOPES: readonly PriceScope[] = ['one_way', 'round_trip_half', 'round_trip_full'];

const IATA_RE = /^[A-Z]{3}$/;

export function FlightForm({ mode, segment, onClose }: FlightFormProps) {
  const t = useT();
  const state = useStore();
  const actions = useActions();

  const [from, setFrom] = useState(segment?.from ?? '');
  const [to, setTo] = useState(segment?.to ?? '');
  const [fromTz, setFromTz] = useState(segment?.fromTz ?? '');
  const [toTz, setToTz] = useState(segment?.toTz ?? '');
  const [departLocal, setDepartLocal] = useState(segment?.departLocal ?? '');
  const [arriveLocal, setArriveLocal] = useState(segment?.arriveLocal ?? '');
  const [carrier, setCarrier] = useState(segment?.carrier ?? '');
  const [flightNumber, setFlightNumber] = useState(segment?.flightNumber ?? '');
  const [url, setUrl] = useState(segment?.url ?? '');
  const [amount, setAmount] = useState(segment ? String(fromMinor(segment.price.amount)) : '');
  const [currency, setCurrency] = useState(segment?.price.currency ?? 'USD');
  const [priceScope, setPriceScope] = useState<PriceScope>(segment?.priceScope ?? 'one_way');
  const [linkedTo, setLinkedTo] = useState(segment?.linkedTo ?? '');
  const [note, setNote] = useState(segment?.note ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const refs = useRef<Record<string, HTMLElement | null>>({});

  const otherFlights = Object.values(state.library).filter(
    (s): s is FlightSegment => s.kind === 'flight' && s.id !== segment?.id,
  );

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    const fromU = from.toUpperCase();
    const toU = to.toUpperCase();
    if (!IATA_RE.test(fromU)) errs.from = t('error.iata');
    if (!IATA_RE.test(toU)) errs.to = t('error.iata');
    if (!departLocal) errs.departLocal = t('error.departRequired');
    if (!arriveLocal) errs.arriveLocal = t('error.arriveRequired');
    if (departLocal && arriveLocal && fromTz && toTz) {
      const depMs = toUtcMillis(departLocal, fromTz);
      const arrMs = toUtcMillis(arriveLocal, toTz);
      if (depMs !== null && arrMs !== null && arrMs <= depMs) {
        errs.arriveLocal = t('error.arriveBeforeDepart');
      }
    }
    const amountNum = parseFloat(amount.replace(',', '.'));
    if (amount.trim() === '' || Number.isNaN(amountNum) || amountNum < 0) {
      errs.amount = t('error.priceNegative');
    }
    return errs;
  }

  function focusFirstError(errs: Record<string, string>) {
    const order = ['from', 'to', 'departLocal', 'arriveLocal', 'amount'];
    for (const key of order) {
      if (errs[key]) {
        refs.current[key]?.focus();
        return;
      }
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }

    const id = segment?.id ?? newId('f');
    const nextLinkedTo = priceScope === 'round_trip_full' && linkedTo ? linkedTo : undefined;
    const built: FlightSegment = {
      kind: 'flight',
      id,
      from: from.toUpperCase(),
      to: to.toUpperCase(),
      fromTz,
      toTz,
      departLocal,
      arriveLocal,
      carrier: carrier.trim(),
      flightNumber: flightNumber.trim() || undefined,
      url: url.trim() || undefined,
      price: money(toMinor(amount), currency),
      priceScope,
      linkedTo: nextLinkedTo,
      note: note.trim() || undefined,
    };

    if (mode === 'edit' && segment) {
      actions.updateSegment(id, built);
    } else {
      actions.addSegment(built);
    }
    syncLinkedTo(actions, id, segment?.linkedTo, nextLinkedTo);
    onClose();
  }

  const fromTzWarning = !fromTz ? t('warn.tzMissing') : undefined;
  const toTzWarning = !toTz ? t('warn.tzMissing') : undefined;

  return (
    <form onSubmit={handleSubmit}>
      <div className={fieldStyles.row}>
        <TextField
          id="flight-from"
          label={t('field.fromIata')}
          value={from}
          onChange={setFrom}
          uppercase
          maxLength={3}
          required
          autoFocus
          placeholder="LCA"
          error={errors.from}
          inputRef={(el) => {
            refs.current.from = el;
          }}
        />
        <TextField
          id="flight-to"
          label={t('field.toIata')}
          value={to}
          onChange={setTo}
          uppercase
          maxLength={3}
          required
          placeholder="EVN"
          error={errors.to}
          inputRef={(el) => {
            refs.current.to = el;
          }}
        />
      </div>
      <div className={fieldStyles.row}>
        <TextField
          id="flight-from-tz"
          label={t('field.tzFrom')}
          value={fromTz}
          onChange={setFromTz}
          warning={fromTzWarning}
          placeholder="Europe/Nicosia"
          listId="tz-list"
          list={COMMON_TIMEZONES}
        />
        <TextField
          id="flight-to-tz"
          label={t('field.tzTo')}
          value={toTz}
          onChange={setToTz}
          warning={toTzWarning}
          placeholder="Asia/Yerevan"
          listId="tz-list-2"
          list={COMMON_TIMEZONES}
        />
      </div>
      <div className={fieldStyles.row}>
        <DateTimeField
          id="flight-depart"
          label={t('field.departLocal')}
          value={departLocal}
          onChange={setDepartLocal}
          required
          error={errors.departLocal}
          inputRef={(el) => {
            refs.current.departLocal = el;
          }}
        />
        <DateTimeField
          id="flight-arrive"
          label={t('field.arriveLocal')}
          value={arriveLocal}
          onChange={setArriveLocal}
          required
          error={errors.arriveLocal}
          inputRef={(el) => {
            refs.current.arriveLocal = el;
          }}
        />
      </div>
      <div className={fieldStyles.row}>
        {/* Перевозчик необязателен: рейс часто заносят из подборки, где
            авиакомпания ещё не выбрана, а маршрут и цена уже известны. */}
        <TextField
          id="flight-carrier"
          label={t('field.carrier')}
          value={carrier}
          onChange={setCarrier}
          placeholder={t('form.optional')}
          inputRef={(el) => {
            refs.current.carrier = el;
          }}
        />
        <TextField
          id="flight-number"
          label={t('field.flightNumber')}
          value={flightNumber}
          onChange={setFlightNumber}
          placeholder={t('form.optional')}
        />
      </div>
      <TextField
        id="flight-url"
        label={t('field.url')}
        value={url}
        onChange={setUrl}
        placeholder={t('form.optional')}
        hint={t('field.url.hint')}
      />
      <MoneyField
        idPrefix="flight-price"
        label={t('field.price')}
        amount={amount}
        onAmountChange={setAmount}
        currency={currency}
        onCurrencyChange={setCurrency}
        required
        error={errors.amount}
        inputRef={(el) => {
          refs.current.amount = el;
        }}
      />
      <SelectField
        id="flight-price-scope"
        label={t('field.priceScope')}
        value={priceScope}
        onChange={(v) => setPriceScope(v as PriceScope)}
        options={PRICE_SCOPES.map((scope) => ({ value: scope, label: t(`scope.${scope}`) }))}
      />
      {priceScope === 'round_trip_full' && (
        <SelectField
          id="flight-linked-to"
          label={t('field.linked')}
          value={linkedTo}
          onChange={setLinkedTo}
          placeholder={t('form.none')}
          options={otherFlights.map((f) => ({ value: f.id, label: flightLongLabel(f) }))}
          hint={
            linkedTo
              ? t('scope.pairNote')
              : undefined
          }
        />
      )}
      <NoteField id="flight-note" value={note} onChange={setNote} />
      <div className={formShell.footer}>
        <button type="button" className="btn" onClick={onClose}>
          {t('form.cancel')}
        </button>
        <button type="submit" className="btn btn-primary">
          {t('form.save')}
        </button>
      </div>
    </form>
  );
}
